import * as React from "react";

import { datadogRum } from "@datadog/browser-rum";

import { rememberFailedAssetUrl } from "utils/asset-cache-repair.util";
import { importChunkUrl } from "utils/import-chunk-url.util";
import { reloadPage } from "utils/reload-page.util";

// LD flags are only readable from React hooks, but the import factory runs
// outside React — Routes syncs the chunkLoadRecovery flag into this module.
let chunkLoadRecoveryEnabled = false;

export function setChunkLoadRecoveryEnabled(enabled: boolean) {
  chunkLoadRecoveryEnabled = enabled;
}

// Chunk-load failure phrasings across Chrome / Firefox / Safari, plus Vite's
// CSS preload failure. RUM (ENG-1479) shows all three browsers affected.
const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading chunk [\d\w-]* failed/i,
  /unable to preload css/i
];

// Shared copy for the error boundaries' chunk-failure state (a surviving
// chunk error after the auto-reload usually means a device-local network
// problem, not a deploy).
export const CHUNK_LOAD_ERROR_MESSAGE =
  "We couldn't load this page — this usually means a network issue on this device. Check your connection and click Retry.";

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error) || !error.message) return false;
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) =>
    pattern.test(error.message)
  );
}

const storageKey = (chunkName: string) => `suno_chunk_reload:${chunkName}`;

// Retry markers live in localStorage, not sessionStorage. sessionStorage is
// scoped to a single tab and is wiped on browser restart / new tab, so a user
// who relaunched the browser (or opened a fresh tab) after a crash started
// with an empty budget every time — the "already retried" escalation never
// fired and they stayed in a reload/relaunch loop instead of reaching the
// error screen (SN-18813). localStorage survives restarts and is shared across
// tabs; the TTL keeps the budget self-healing so a later deploy still earns a
// fresh auto-reload.
const RELOAD_MARKER_TTL_MS = 30 * 60 * 1000;

// Returned when a storage read is blocked (e.g. partitioned/denied storage).
// Callers treat any truthy marker as "already attempted", so failing safe to
// this value stops reloading instead of looping forever on a client whose
// writes succeed but reads throw.
const MARKER_UNREADABLE = "unreadable";

function clearMarker(chunkName: string) {
  try {
    localStorage.removeItem(storageKey(chunkName));
  } catch {
    // ignore — worst case the next failure rethrows instead of reloading
  }
}

function getMarker(chunkName: string): string | null {
  try {
    const raw = localStorage.getItem(storageKey(chunkName));
    if (raw === null) return null;
    const setAt = Date.parse(raw);
    if (!Number.isNaN(setAt) && Date.now() - setAt > RELOAD_MARKER_TTL_MS) {
      clearMarker(chunkName);
      return null;
    }
    return raw;
  } catch {
    return MARKER_UNREADABLE;
  }
}

function setMarker(chunkName: string): boolean {
  try {
    localStorage.setItem(storageKey(chunkName), new Date().toISOString());
    return true;
  } catch {
    return false;
  }
}

// The browser's own chunk-load error message includes the failing module's
// full URL — reuse it rather than re-deriving it from the import specifier.
function extractFailedChunkUrl(error: unknown): string | undefined {
  if (!(error instanceof Error) || !error.message) return undefined;
  return error.message.match(/https?:\/\/[^\s)]+/)?.[0];
}

// A cache-busted *document* reload doesn't help if the CHUNK's own URL is
// what got cached as an error (ENG-1829/ENG-1830): the freshly reloaded page
// still asks for the same, still-poisoned chunk URL. Presenting a URL the
// cache has never seen bypasses the entry for this one import; the entry
// itself (and any poisoned dependency of this chunk, which this retry can't
// reach) is overwritten by reloadPage's cache repair (DEV-3172).
function bustChunkUrl(rawUrl: string): string | undefined {
  try {
    const url = new URL(rawUrl);
    url.searchParams.set("_cb", Date.now().toString(36));
    return url.toString();
  } catch {
    return undefined;
  }
}

// A chunk-load error's URL may belong to a shared dependency chunk rather
// than the one this import actually wants (the browser reports whichever
// URL failed mid-fetch, not the top-level specifier) — busting and importing
// that URL directly would hand React.lazy the dependency's module instead of
// `{ default: Component }`. Rather than trying to predict this from the URL
// string beforehand, validate what actually came back.
function hasDefaultExport(module: unknown): module is { default: unknown } {
  return typeof module === "object" && module !== null && "default" in module;
}

type RetryingImportOptions = {
  // Whether a failed cache-busted retry may fall back to the one guarded
  // document reload. The scanner dialog opts out (ENG-1867): a reload there
  // can be vetoed by the Ambient Scribe's beforeunload guard, which parks the
  // lazy on a never-settling promise and poisons it for the tab's lifetime
  // (DEV-2806) — it rethrows into its dialog-scoped boundary instead.
  reloadFallback?: boolean;
  // Whether a retried module must expose `default` to count as recovered.
  // True for React.lazy (see hasDefaultExport). A plain library module —
  // `@sendbird/chat/groupChannel` exports only `{ GroupChannelModule }` —
  // legitimately has none, and judging that a failed retry would send a
  // working recovery down the reload path instead (importWithRetry).
  expectDefaultExport?: boolean;
};

/**
 * Wraps a lazy-route import so a deploy-time chunk hash mismatch recovers
 * without a page reload where possible (a cache-busted retry of the failed
 * chunk's own URL, ENG-1830), falling back to exactly one automatic
 * document reload per chunk (guarded by a TTL'd localStorage marker that
 * survives a browser restart) if that also fails. A third failure rethrows
 * to the route error boundary. Non-chunk errors always rethrow untouched.
 * `reloadFallback: false` drops the reload rung: after a failed cache-busted
 * retry the ORIGINAL chunk error rethrows (so boundaries that branch on
 * `isChunkLoadError` still classify it correctly).
 */
export function createRetryingImport<T>(
  importFn: () => Promise<T>,
  chunkName: string,
  {
    reloadFallback = true,
    expectDefaultExport = true
  }: RetryingImportOptions = {}
): () => Promise<T> {
  return async () => {
    try {
      const module = await importFn();
      if (getMarker(chunkName)) {
        clearMarker(chunkName);
        datadogRum.addAction("chunk_load_recovered", { chunkName });
      }
      return module;
    } catch (error) {
      if (!chunkLoadRecoveryEnabled || !isChunkLoadError(error)) {
        throw error;
      }

      const failedUrl = extractFailedChunkUrl(error);
      rememberFailedAssetUrl(failedUrl);
      const bustedUrl = failedUrl ? bustChunkUrl(failedUrl) : undefined;
      if (bustedUrl) {
        try {
          const module = await importChunkUrl<T>(bustedUrl);
          if (expectDefaultExport && !hasDefaultExport(module)) {
            // Resolved, but not to this chunk's shape — most likely
            // failedUrl belonged to a dependency chunk. Treat exactly like a
            // failed retry: fall through to the reload path below.
            throw new Error("retried chunk has no default export");
          }
          datadogRum.addAction("chunk_load_recovered_inline", {
            chunkName,
            bustedUrl
          });
          return module as T;
        } catch {
          // Cache-busted retry also failed (or resolved to the wrong shape)
          // — fall through to the existing document-reload recovery below,
          // unchanged.
        }
      }

      // No reload budget for this chunk: surface the failure to its own
      // boundary. No extra telemetry here — the scanner boundary already
      // reports via datadogRum.addError, and inline-retry successes are
      // visible as chunk_load_recovered_inline.
      if (!reloadFallback) {
        throw error;
      }

      if (getMarker(chunkName)) {
        datadogRum.addAction("chunk_load_failed_after_reload", {
          chunkName,
          errorMessage: (error as Error).message,
          failedUrl
        });
        throw error;
      }
      // Never reload without a persisted guard — that risks a reload loop.
      if (!setMarker(chunkName)) {
        throw error;
      }
      // failedUrl is logged here too (not just on the inline-recovery action
      // above) so a chunk whose busted retry consistently fails or resolves
      // to the wrong shape is still queryable in RUM, not indistinguishable
      // from "no URL was in the error at all" (Safari never includes one).
      datadogRum.addAction("chunk_reload_attempt", {
        chunkName,
        attempt: 1,
        errorMessage: (error as Error).message,
        failedUrl
      });
      reloadPage();
      // Keep the import pending so Suspense shows its fallback while the
      // page reloads, instead of flashing the error boundary.
      return new Promise<T>(() => {});
    }
  };
}

const PRELOAD_MARKER_PREFIX = "vite-preload:";

// Build SHA baked in by Vite's `define`; absent under Jest / non-Vite tooling.
// Exported for ENG-747's stale-build-detection poll (use-build-version-check.hook.ts),
// which compares this same value against version.json's `sha` — one source of truth
// for "what build is this tab running" rather than two independent __APP_VERSION__ reads.
export function getAppVersion(): string {
  return typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
}

// Build ordinal baked in by Vite's `define` (see vite.config.mts). Falls back to null,
// not 0, under Jest / non-Vite tooling — 0 is a real ordinal (the repo's first commit)
// and must not be indistinguishable from "unknown".
export function getAppBuildNumber(): number | null {
  return typeof __APP_BUILD_NUMBER__ !== "undefined"
    ? __APP_BUILD_NUMBER__
    : null;
}

const preloadChunkName = () => `${PRELOAD_MARKER_PREFIX}${getAppVersion()}`;

const extractChunkUrl = (event: Event): string | undefined =>
  // Vite attaches the original error as `payload` on the event.
  extractFailedChunkUrl((event as Event & { payload?: unknown }).payload);

/**
 * Drop reload markers left behind by OTHER app versions so each deploy gets
 * a fresh auto-reload budget. Called once at boot (index.tsx). Deliberately
 * does NOT clear the current version's marker: the boot right after an
 * auto-reload looks "successful" before the chunk fails again, so clearing
 * on every boot would re-arm the DEV-2147 reload loop.
 */
export function pruneStaleChunkReloadMarkers() {
  try {
    const fullPrefix = storageKey(PRELOAD_MARKER_PREFIX);
    const currentKey = storageKey(preloadChunkName());
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(fullPrefix) && key !== currentKey) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore — stale markers only delay a future auto-reload, never loop
  }
}

/**
 * Guarded replacement for the bare `vite:preloadError -> reload` listener
 * (SN-8541). A persistent local fetch failure (proxy/AV/flaky network) used
 * to reload forever, locking the user out (DEV-2147). Exactly one automatic
 * reload per app version (persisted in localStorage with a TTL, version-keyed
 * so the next deploy self-heals again); once the budget is spent it propagates,
 * landing in lazyWithRetry / the route error boundaries.
 *
 * Deliberately NOT gated behind chunkLoadRecovery: the loop happens during
 * early boot, before LaunchDarkly resolves (flags default false there), so a
 * flag-gated cap would be off exactly when it's needed. The cap is a safety
 * fix, strictly safer than the unconditional reload in every flag state.
 */
export function handleVitePreloadError(event: Event) {
  const chunkName = preloadChunkName();
  rememberFailedAssetUrl(extractChunkUrl(event));
  if (getMarker(chunkName)) {
    datadogRum.addAction("chunk_reload_exhausted", {
      appVersion: getAppVersion(),
      chunkUrl: extractChunkUrl(event)
    });
    // No preventDefault: Vite rethrows, the failing import rejects into the
    // error boundaries (friendly retry screen) instead of reloading again.
    return;
  }
  // Never reload without a persisted guard — that risks a reload loop.
  if (!setMarker(chunkName)) {
    return;
  }
  datadogRum.addAction("chunk_reload_attempt", {
    appVersion: getAppVersion(),
    attempt: 1,
    chunkUrl: extractChunkUrl(event)
  });
  // Let Vite rethrow (no preventDefault): cancelling the event makes
  // __vitePreload resolve to `undefined`, so React.lazy crashes on
  // `undefined.default` instead of surfacing a clean chunk-load error
  // (SN-18714). The rethrow reaches createRetryingImport / the boundaries.
  reloadPage();
}

/**
 * Same chunk-load recovery for a dynamic `import()` that is NOT a React.lazy
 * component — the heavy libraries loaded on demand (Sendbird chat, xlsx,
 * pspdfkit, @react-pdf/renderer, emoji-mart). A bare `import()` has no
 * recovery at all: one transient chunk failure leaves that feature dead for
 * the rest of the session (chat never connects, the export button does
 * nothing) and surfaces as a RUM error, even when the file is on the server
 * and a second request would succeed (DEV-3172 follow-up).
 *
 * Deliberately different from the route version on two points:
 * - **no reload rung.** These imports run under a user who clicked Export, or
 *   in the background while they work; reloading the document would throw
 *   away what they were doing. A failed retry rethrows the ORIGINAL error, so
 *   the caller's existing `catch` behaves exactly as it does today.
 * - **no default-export requirement**, since a library module often has none.
 */
export function importWithRetry<T>(
  importFn: () => Promise<T>,
  chunkName: string
): Promise<T> {
  return createRetryingImport(importFn, chunkName, {
    reloadFallback: false,
    expectDefaultExport: false
  })();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  chunkName: string,
  options?: RetryingImportOptions
) {
  return React.lazy(createRetryingImport(importFn, chunkName, options));
}
